import React, { useState, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  MoreVertical,
  Tag,
  Search,
  LayoutGrid,
  List,
  Loader2,
  Eye,
  MessageCircle,
} from 'lucide-react';
import { ColorPicker } from '../components/ColorPicker/ColorPicker';
import { labelsAPI } from '../services/api';
import { PermissionGuard } from '../components/Permissions/PermissionGuard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardAction } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { toast } from '../components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Label } from '../components/ui/label';

interface LabelItem {
  id: number;
  title: string;
  description: string | null;
  color: string;
  show_on_sidebar: boolean;
  conversation_count: number;
}

interface LabelFormData {
  title: string;
  description: string;
  color: string;
  show_on_sidebar: boolean;
}

const LabelsPage: React.FC = () => {
  const [labels, setLabels] = useState<LabelItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLabel, setEditingLabel] = useState<LabelItem | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSidebar, setFilterSidebar] = useState<string>('all');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [labelToDelete, setLabelToDelete] = useState<LabelItem | null>(null);

  const [formData, setFormData] = useState<LabelFormData>({
    title: '',
    description: '',
    color: 'hsl(var(--primary))',
    show_on_sidebar: false,
  });

  useEffect(() => {
    fetchLabels();
  }, []);

  const fetchLabels = async () => {
    try {
      setLoading(true);
      const data = await labelsAPI.getLabels();
      setLabels(data);
    } catch (error: any) {
      console.error('Error fetching labels:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to fetch labels', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingLabel) {
        await labelsAPI.updateLabel(editingLabel.id, formData);
        toast({ title: 'Label updated', description: 'Label updated successfully' });
      } else {
        await labelsAPI.createLabel(formData);
        toast({ title: 'Label created', description: 'Label created successfully' });
      }
      fetchLabels();
      handleCloseDialog();
    } catch (error: any) {
      console.error('Error saving label:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save label', variant: 'destructive' });
    }
  };

  const handleDelete = async (label: LabelItem) => {
    try {
      await labelsAPI.deleteLabel(label.id);
      toast({ title: 'Label deleted', description: 'Label deleted successfully' });
      fetchLabels();
    } catch (error: any) {
      console.error('Error deleting label:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete label', variant: 'destructive' });
    } finally {
      setDeleteConfirmOpen(false);
      setLabelToDelete(null);
    }
  };

  const handleOpenDialog = (label?: LabelItem) => {
    if (label) {
      setEditingLabel(label);
      setFormData({
        title: label.title,
        description: label.description || '',
        color: label.color,
        show_on_sidebar: label.show_on_sidebar,
      });
    } else {
      setEditingLabel(null);
      setFormData({
        title: '',
        description: '',
        color: 'hsl(var(--primary))',
        show_on_sidebar: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLabel(null);
  };

  const filteredLabels = labels.filter((label) => {
    const matchesSearch =
      label.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (label.description && label.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter =
      filterSidebar === 'all' ||
      (filterSidebar === 'true' && label.show_on_sidebar) ||
      (filterSidebar === 'false' && !label.show_on_sidebar);
    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: labels.length,
    sidebar: labels.filter((l) => l.show_on_sidebar).length,
    totalConversations: labels.reduce((sum, l) => sum + l.conversation_count, 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Labels</h1>
          <p className="text-sm text-muted-foreground">Organize conversations with custom labels</p>
        </div>
        <PermissionGuard permission="labels:create">
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="h-4 w-4 mr-2" />
            Add Label
          </Button>
        </PermissionGuard>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm @container/card">
          <CardHeader>
            <CardDescription>Total Labels</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">{stats.total}</CardTitle>
            <CardAction>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Tag className="h-5 w-5 text-primary" />
              </div>
            </CardAction>
          </CardHeader>
        </Card>
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm @container/card">
          <CardHeader>
            <CardDescription>Sidebar Labels</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">{stats.sidebar}</CardTitle>
            <CardAction>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Eye className="h-5 w-5 text-primary" />
              </div>
            </CardAction>
          </CardHeader>
        </Card>
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm @container/card">
          <CardHeader>
            <CardDescription>Total Conversations</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">{stats.totalConversations}</CardTitle>
            <CardAction>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <MessageCircle className="h-5 w-5 text-primary" />
              </div>
            </CardAction>
          </CardHeader>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search labels..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterSidebar} onValueChange={(v) => setFilterSidebar(v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by sidebar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Labels</SelectItem>
                <SelectItem value="true">Sidebar Only</SelectItem>
                <SelectItem value="false">Non-Sidebar</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Button
                variant={viewMode === 'cards' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setViewMode('cards')}
                className="h-9 w-9"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setViewMode('table')}
                className="h-9 w-9"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Labels Content */}
      {filteredLabels.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Tag className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-muted-foreground">
              {searchTerm ? 'No labels found' : 'No labels yet'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">
              {searchTerm
                ? 'Try adjusting your search or filters'
                : 'Create your first label to organize conversations'}
            </p>
            {!searchTerm && (
              <PermissionGuard permission="labels:create">
                <Button onClick={() => handleOpenDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Label
                </Button>
              </PermissionGuard>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'cards' ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredLabels.map((label) => (
            <Card key={label.id} className="relative overflow-hidden">
              <div
                className="h-1 w-full absolute top-0 left-0"
                style={{ backgroundColor: label.color }}
              />
              <CardContent className="pt-7">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="h-8 w-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: label.color }}
                    >
                      <Tag className="h-4 w-4 text-white" />
                    </div>
                    <p className="text-sm font-medium">{label.title}</p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <PermissionGuard permission="labels:edit">
                        <DropdownMenuItem onClick={() => handleOpenDialog(label)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                      </PermissionGuard>
                      <PermissionGuard permission="labels:delete">
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => {
                            setLabelToDelete(label);
                            setDeleteConfirmOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </PermissionGuard>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {label.description && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{label.description}</p>
                )}

                <Separator className="my-3" />

                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="text-xs">
                    {label.conversation_count} conversations
                  </Badge>
                  {label.show_on_sidebar && (
                    <Badge variant="secondary" className="text-xs">Sidebar</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead>Conversations</TableHead>
                  <TableHead>Sidebar</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLabels.map((label) => (
                  <TableRow key={label.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: label.color }}
                        >
                          <Tag className="h-3 w-3 text-white" />
                        </div>
                        <span className="text-sm font-medium">{label.title}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground line-clamp-1">{label.description || '—'}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-4 w-4 rounded-full border border-border"
                          style={{ backgroundColor: label.color }}
                        />
                        <span className="text-xs text-muted-foreground font-mono">{label.color}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{label.conversation_count}</Badge>
                    </TableCell>
                    <TableCell>
                      <Checkbox checked={label.show_on_sidebar} disabled />
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <PermissionGuard permission="labels:edit">
                            <DropdownMenuItem onClick={() => handleOpenDialog(label)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                          </PermissionGuard>
                          <PermissionGuard permission="labels:delete">
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => {
                                setLabelToDelete(label);
                                setDeleteConfirmOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </PermissionGuard>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Mobile FAB */}
      <div className="fixed bottom-6 right-6 sm:hidden">
        <PermissionGuard permission="labels:create">
          <Button
            size="icon"
            className="h-14 w-14 rounded-full shadow-lg"
            onClick={() => handleOpenDialog()}
          >
            <Plus className="h-6 w-6" />
          </Button>
        </PermissionGuard>
      </div>

      {/* Label Dialog */}
      <Dialog open={openDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingLabel ? 'Edit Label' : 'Add New Label'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="label-title">Label Title *</Label>
              <Input
                id="label-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter label title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="label-description">Description</Label>
              <textarea
                id="label-description"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
            <div className="space-y-2">
              <Label>Label Color</Label>
              <ColorPicker
                color={formData.color}
                onChange={(color) => setFormData({ ...formData, color })}
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="show-sidebar"
                checked={formData.show_on_sidebar}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, show_on_sidebar: checked })
                }
              />
              <Label htmlFor="show-sidebar" className="cursor-pointer">
                Show on sidebar
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!formData.title.trim()}>
              {editingLabel ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Label</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the label "{labelToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDeleteConfirmOpen(false); setLabelToDelete(null); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => labelToDelete && handleDelete(labelToDelete)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LabelsPage;
